import React from 'react';
import { theme, Typography, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import {
  useHasPermission,
  useHasAnyPermission,
  useHasAllPermissions,
  isSuperUser,
} from '../../utils/permissionUtils';
const { Text } = Typography;

/**
 * PolicyCard — permission-gated navigation card.
 *
 * Variants:
 *  - default    : icon top, label below (dashboard / control-panel grid)
 *  - horizontal : icon left, label + description right (settings list)
 *
 * @param {string|string[]} permission
 * @param {boolean}         requireAll
 * @param {string}          to           – navigation path
 * @param {string}          icon         – icon name from registry (e.g. "MdSettings")
 * @param {string}          title
 * @param {string}          description  – shown in horizontal variant & tooltip
 * @param {string}          color        – accent / icon colour
 * @param {number}          iconSize
 * @param {'default'|'horizontal'} variant
 * @param {React.ReactNode} children     – overrides default content
 * @param {object}          style        – extra wrapper styles
 */
const PolicyCard = ({
  permission,
  requireAll = false,
  to,
  icon,
  title,
  description,
  color,
  iconSize,
  variant = 'default',
  children,
  style,
}) => {
  const hasSingle = useHasPermission(Array.isArray(permission) ? null : permission);
  const hasAny    = useHasAnyPermission(Array.isArray(permission) ? permission : []);
  const hasAll    = useHasAllPermissions(Array.isArray(permission) ? permission : []);

  const { token } = theme.useToken();

  const isUnrestricted =
    !permission || (Array.isArray(permission) && permission.length === 0);

  if (!isUnrestricted && !isSuperUser()) {
    const hasAccess = Array.isArray(permission)
      ? (requireAll ? hasAll : hasAny)
      : hasSingle;

    if (!hasAccess) return null;
  }

  const accentColor      = color || token.colorPrimary;
  const resolvedIconSize = iconSize ?? (variant === 'horizontal' ? 22 : 26);
  const iconEl = icon
    ? React.isValidElement(icon)
      ? React.cloneElement(icon, { style: { fontSize: resolvedIconSize, color: accentColor } })
      : null
    : null;

  const isHorizontal = variant === 'horizontal';

  const cardStyle = {
    display: 'flex',
    flexDirection: isHorizontal ? 'row' : 'column',
    alignItems: 'center',
    justifyContent: isHorizontal ? 'flex-start' : 'center',
    gap: isHorizontal ? 10 : 6,
    padding: isHorizontal ? '10px 12px' : '12px 8px',
    borderRadius: token.borderRadiusLG,
    border: `1px solid ${token.colorBorderSecondary}`,
    background: token.colorBgContainer,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textDecoration: 'none',
    color: 'inherit',
    minHeight: isHorizontal ? 52 : 80,
    width: '100%',
    boxSizing: 'border-box',
    ...style,
  };

  const handleMouseEnter = (e) => {
    e.currentTarget.style.boxShadow = `0 2px 8px ${accentColor}18`;
    e.currentTarget.style.transform = 'translateY(-1px)';
  };
  const handleMouseLeave = (e) => {
    e.currentTarget.style.boxShadow = 'none';
    e.currentTarget.style.transform = 'none';
  };

  const content = children || (
    <>
      {iconEl && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: isHorizontal ? 36 : 40,
            height: isHorizontal ? 36 : 40,
            borderRadius: token.borderRadiusSM,
            background: `${accentColor}18`,
            flexShrink: 0,
          }}
        >
          {iconEl}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isHorizontal ? 'flex-start' : 'center',
          gap: 1,
          overflow: 'hidden',
          flex: isHorizontal ? 1 : undefined,
        }}
      >
        {title && (
          <Text
            strong
            style={{
              fontSize: isHorizontal ? token.fontSize : token.fontSizeSM,
              lineHeight: 1.35,
              textAlign: isHorizontal ? 'left' : 'center',
              whiteSpace: isHorizontal ? 'nowrap' : 'normal',
              overflow: isHorizontal ? 'hidden' : undefined,
              textOverflow: isHorizontal ? 'ellipsis' : undefined,
              wordBreak: isHorizontal ? undefined : 'break-word',
              display: 'block',
              maxWidth: '100%',
            }}
          >
            {title}
          </Text>
        )}
        {isHorizontal && description && (
          <Text
            type="secondary"
            style={{
              fontSize: token.fontSizeSM - 1,
              lineHeight: 1.3,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
              maxWidth: '100%',
            }}
          >
            {description}
          </Text>
        )}
      </div>
    </>
  );

  const inner = to ? (
    <Link
      to={to}
      style={cardStyle}
      title={description}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {content}
    </Link>
  ) : (
    <div
      style={cardStyle}
      title={description}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {content}
    </div>
  );

  return !isHorizontal && description
    ? <Tooltip title={description} placement="top">{inner}</Tooltip>
    : inner;
};

export default PolicyCard;
